import React, { useEffect, useMemo, useState } from 'react';

const emptyMember = () => ({ name: '', role: '', description: '', fullBio: '', image: '', order: 0 });
const deepClone = (o) => JSON.parse(JSON.stringify(o));

export default function CommitteeAdmin() {
  const [members, setMembers] = useState([]);
  const [original, setOriginal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [draft, setDraft] = useState(emptyMember());

  const isDirty = useMemo(() => JSON.stringify(original) !== JSON.stringify(members), [original, members]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/committee');
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMembers(deepClone(data));
      setOriginal(deepClone(data));
    } catch (e) {
      setError(e.message || 'Failed to load committee.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const publish = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch('/api/committee', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(members),
      });
      if (!res.ok) throw new Error(await res.text());
      const saved = await res.json();
      setMembers(deepClone(saved));
      setOriginal(deepClone(saved));
      setMessage('Published successfully.');
    } catch (e) {
      setError(e.message || 'Failed to publish.');
    } finally {
      setSaving(false);
    }
  };

  const validate = () => {
    if (!draft.name.trim()) return 'Name is required.';
    if (!draft.role.trim()) return 'Role is required.';
    return '';
  };

  const saveDraft = () => {
    const err = validate();
    if (err) { alert(err); return; }
    const entry = { ...draft, order: Number(draft.order) || 0 };
    if (editingIndex === -1) {
      setMembers((prev) => [...prev, entry]);
    } else {
      setMembers((prev) => prev.map((m, i) => i === editingIndex ? entry : m));
    }
    setEditingIndex(null);
    setDraft(emptyMember());
  };

  const deleteMember = (idx) => {
    if (!window.confirm(`Remove "${members[idx].name}"?`)) return;
    setMembers((prev) => prev.filter((_, i) => i !== idx));
    if (editingIndex === idx) { setEditingIndex(null); setDraft(emptyMember()); }
  };

  const move = (idx, dir) => {
    const arr = [...members];
    const swap = idx + dir;
    if (swap < 0 || swap >= arr.length) return;
    [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
    setMembers(arr);
  };

  if (loading) return <div style={{ padding: 20 }}>Loading…</div>;

  return (
    <div>
      <div className="admin-header">
        <div>
          <h2>Committee</h2>
          <p>Manage committee members shown on the public Committee page.</p>
        </div>
        <div className="admin-actions">
          <button className="btn btn-outline btn-sm" onClick={load} disabled={saving}>Reload</button>
          <button className="btn btn-outline btn-sm" onClick={() => { setEditingIndex(-1); setDraft(emptyMember()); }} disabled={saving}>+ Add member</button>
          <button className="btn btn-primary btn-sm" onClick={publish} disabled={!isDirty || saving}>
            {saving ? 'Publishing…' : 'Publish changes'}
          </button>
        </div>
      </div>

      {error && <div className="admin-msg-error">{error}</div>}
      {message && <div className="admin-msg-success">{message}</div>}

      {editingIndex !== null && (
        <div className="admin-form">
          <h3>{editingIndex === -1 ? 'Add member' : 'Edit member'}</h3>
          <div className="admin-form-grid">
            <label>
              <span>Name *</span>
              <input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
            </label>
            <label>
              <span>Role *</span>
              <input value={draft.role} onChange={(e) => setDraft((d) => ({ ...d, role: e.target.value }))} placeholder="e.g. Chairperson" />
            </label>
            <label className="full-width">
              <span>Short description</span>
              <input value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} placeholder="One-line summary" />
            </label>
            <label className="full-width">
              <span>Full bio</span>
              <textarea rows={4} value={draft.fullBio} onChange={(e) => setDraft((d) => ({ ...d, fullBio: e.target.value }))} />
            </label>
            <label className="full-width">
              <span>Photo URL</span>
              <input value={draft.image} onChange={(e) => setDraft((d) => ({ ...d, image: e.target.value }))} placeholder="https://…" />
            </label>
            <label>
              <span>Display order</span>
              <input type="number" value={draft.order} onChange={(e) => setDraft((d) => ({ ...d, order: e.target.value }))} min={0} />
            </label>
          </div>
          <div className="admin-form-actions">
            <button className="btn btn-outline btn-sm" onClick={() => { setEditingIndex(null); setDraft(emptyMember()); }}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={saveDraft}>Save</button>
          </div>
        </div>
      )}

      <div className="admin-list">
        {members.length === 0
          ? <p style={{ color: 'var(--text-muted)' }}>No members yet.</p>
          : members.map((m, i) => (
              <div className="admin-item" key={`${m.name}-${i}`}>
                <div className="admin-item-info">
                  <div className="admin-item-title">{m.name || '(Unnamed)'}</div>
                  <div className="admin-item-meta">
                    <span className="badge">{m.role}</span>
                    {m.description && <span>{m.description}</span>}
                  </div>
                </div>
                <div className="admin-item-btns">
                  <button className="btn btn-outline btn-sm" onClick={() => move(i, -1)} disabled={i === 0} title="Move up">↑</button>
                  <button className="btn btn-outline btn-sm" onClick={() => move(i, 1)} disabled={i === members.length - 1} title="Move down">↓</button>
                  <button className="btn btn-outline btn-sm" onClick={() => { setEditingIndex(i); setDraft({ ...m }); }}>Edit</button>
                  <button className="btn btn-outline btn-sm" onClick={() => deleteMember(i)}>Remove</button>
                </div>
              </div>
            ))
        }
      </div>
    </div>
  );
}
